"use client";

import type {
  GetProductBySlugQuery,
  RentalPlan,
  Variant,
} from "@/api/graphql/generates";
import type { GetOrCreateCartForClerkMutation } from "@/api/pos-service/generates";
import { addCartItem } from "@/api/pos-service/mutations/addCartItem";
import { changeCartRentalPlan } from "@/api/pos-service/mutations/changeCartRentalPlan";
import {
  getGetOrCreateCartForClerkyKeys,
  getOrCreateCartForClerk,
} from "@/api/pos-service/mutations/getOrCreateCart";
import { reCreateCart } from "@/api/pos-service/mutations/reCreateCart";
import { removeCartItem } from "@/api/pos-service/mutations/removeCartItem";
import { useAnalyticsEvents } from "@/hooks/useAnalyticsEvents";
import { useStore } from "@/hooks/useStore";
import { useToast } from "@/hooks/useToast";
import { sendDatadogAction } from "@/modules/lib/observability/datadogUtils";
import { logger } from "@/modules/lib/observability/logger";
import { formatSegmentProductPayload } from "@/utils/tracking";
import {
  type MutateOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { createContext, useContext, useMemo } from "react";
import { useSession } from "./sessionContext";

export const normalizeSerialNumber = (serialNumber?: string) =>
  (serialNumber ?? "").toUpperCase();

export type Product = NonNullable<GetProductBySlugQuery["product"]>;

export type ItemVariant = Partial<Variant>;

export type Plan = RentalPlan;

export type CartItem =
  GetOrCreateCartForClerkMutation["posCartGetOrCreate"]["items"][0];

export interface ICartInfo {
  orderNumber: string;
  guestToken: string;
  total: {
    inCents: number;
    currency: string;
  };
  items: CartItem[];
}

export type ICartContext = ICartInfo & {
  isFetching: boolean;
  totalQuantity: number;
  reCreate: (
    args: {
      orderNumber: string;
    },
    config?: MutateOptions<unknown, unknown, unknown>,
  ) => void;
  add: (
    args: {
      selectedPlan: Plan;
      selectedVariant: Partial<Variant>;
      purchasePrice: number;
      serialNumber: string;
      product: Partial<Product>;
    },
    config?: MutateOptions<unknown, unknown, unknown>,
  ) => void;
  fetchError: boolean;
  remove: (itemId: number) => void;
  update: (args: {
    itemId: number;
    rentalPlan: {
      id: number;
      committedMonths: number;
      price: {
        inCents: number;
        currency: string;
      };
    };
  }) => void;
};

const CartContext = createContext<ICartContext>({
  guestToken: "",
  orderNumber: "",
  items: [],
  isFetching: true,
  total: {
    inCents: 0,
    currency: "EUR",
  },
  totalQuantity: 0,
  fetchError: false,
  add: () => {},
  reCreate: () => {},
  update: () => {},
  remove: () => {},
});

export const CartProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useSession();
  const { store, storeId } = useStore();
  const { addToast } = useToast();
  const t = useTranslations("cart");
  const { sendAnalyticsEvent } = useAnalyticsEvents();

  const queryClient = useQueryClient();
  const cartQueryKey = useMemo(
    () => getGetOrCreateCartForClerkyKeys(store),
    [store],
  );

  const {
    data: cartInfo,
    isLoading,
    error: fetchError,
  } = useQuery<ICartInfo>({
    queryKey: getGetOrCreateCartForClerkyKeys(store),
    queryFn: async () => {
      const res = await getOrCreateCartForClerk({
        storeCode: store,
      });

      const { posCartGetOrCreate } = res;

      return {
        orderNumber: posCartGetOrCreate.orderNumber,
        guestToken: posCartGetOrCreate.guestToken,
        items: posCartGetOrCreate.items,
        total: posCartGetOrCreate.total,
      };
    },
    enabled: !!user?.id && !!store,
    staleTime: Number.POSITIVE_INFINITY,
    retry(failureCount, error) {
      if (error instanceof Error) {
        logger.error("[Cart] failed to get cart", {
          store,
          errorMessage: error.message,
        });
      }
      return failureCount < 3;
    },
  });

  const cart = cartInfo || {
    orderNumber: "",
    guestToken: "",
    items: [],
    total: {
      inCents: 0,
      currency: "EUR",
    },
  };

  const items = cart.items || [];
  const total = cart.total || {
    inCents: 0,
    currency: "EUR",
  };
  const totalQuantity = items.length;

  const { mutate: add } = useMutation({
    mutationFn: async ({
      selectedPlan,
      selectedVariant,
      purchasePrice,
      serialNumber,
    }: {
      selectedPlan: Plan;
      selectedVariant: Partial<Variant>;
      product: Partial<Product>;
      purchasePrice: number;
      serialNumber: string;
    }) => {
      if (
        cart.items.some(({ variant }) => variant.sku === selectedVariant.sku)
      ) {
        throw new Error("Item already in cart");
      }

      return addCartItem({
        input: {
          orderNumber: cart.orderNumber,
          items: [
            {
              quantity: 1,
              rentalPlanDuration: selectedPlan.length.value,
              variantSku: selectedVariant.sku as string,
              offlineOrderPurchasePrice: Number(
                (purchasePrice / 100).toFixed(2),
              ),
              serialNumber: normalizeSerialNumber(serialNumber),
            },
          ],
        },
        storeCode: store,
      });
    },
    onMutate: async ({
      selectedPlan,
      selectedVariant,
      serialNumber,
      product,
    }: {
      selectedPlan: Plan;
      selectedVariant: Partial<Variant>;
      product: Partial<Product>;
      purchasePrice: number;
      serialNumber: string;
    }) => {
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      const previousItems = items;
      const previousTotal = total;

      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        items: [
          ...items,
          {
            id: Math.random(),
            rentalPlan: {
              id: selectedPlan.id,
              price: {
                inCents: selectedPlan.price.inCents,
                currency: selectedPlan.price.currency,
              },
              length: {
                value: selectedPlan.length.value,
              },
            },
            serialNumber: normalizeSerialNumber(serialNumber),
            name: product.name as string,
            variant: {
              sku: selectedVariant.sku as string,
              colorCodes: selectedVariant.colorCodes as string[],
              colorTitle: selectedVariant.colorTitle as string,
              imageUrl: selectedVariant.imageUrl as string,
            },
            product: {
              name: product.name as string,
              slug: product.slug as string,
              logistics: {
                identifier: product.logistics?.identifier || "IMEI",
              },
              rentalPlans:
                product.rentalPlans?.map((plan) => ({
                  id: String(plan.id),
                  price: {
                    inCents: plan.price.inCents,
                    currency: plan.price.currency,
                  },
                  length: {
                    value: plan.length.value,
                  },
                })) || [],
            },
            eans: [],
            upcs: [],
          },
        ],
        total: {
          inCents: total.inCents + selectedPlan.price.inCents,
          currency: selectedPlan.price.currency,
        },
      });

      return { previousItems, previousTotal };
    },
    onError: (error, variables, context) => {
      logger.error("[Cart] Failed to add an item to the cart", {
        store,
        errorMessage: error.message,
        ...context,
        ...variables,
      });
      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        items: context?.previousItems || [],
        total: context?.previousTotal || {
          inCents: 0,
          currency: "EUR",
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
      });
    },
    onSuccess: (
      _,
      { product, selectedPlan, selectedVariant, purchasePrice, serialNumber },
    ) => {
      sendAnalyticsEvent(
        "Product Added to Cart",
        formatSegmentProductPayload(
          product as Product,
          selectedPlan,
          selectedVariant,
          store as string,
          Number(storeId),
        ),
      );

      sendDatadogAction("POS-product-added-to-cart", {
        selectedPlan,
        selectedVariant,
        store,
        product,
        purchasePrice,
        serialNumber: normalizeSerialNumber(serialNumber),
        order_id: cart.orderNumber,
      });
      addToast({
        title: t("add.success:title"),
        message: t("add.success:message"),
        type: "success",
        link: "/cart",
      });
    },
  });

  const { mutate: remove } = useMutation({
    mutationFn: async (itemId: number) => {
      const res = await removeCartItem({
        input: {
          orderNumber: cart.orderNumber,
          itemId,
        },
        storeCode: store,
      });

      return res;
    },
    onMutate: async (itemId: number) => {
      const previousItems = items;
      const previousTotal = total;

      const itemIndex = items.findIndex((item) => item.id === itemId);
      const item = items[itemIndex];
      items.splice(itemIndex, 1);

      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        total: {
          ...total,
          inCents: total.inCents - item.rentalPlan.price.inCents,
        },
        items,
      });

      return { previousItems, previousTotal };
    },
    onError: (error, variables, context) => {
      logger.error("[Cart] failed to remove item from cart", {
        store,
        errorMessage: error.message,
        variables,
        ...context,
      });
      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        total: context?.previousTotal || total,
        items: context?.previousItems || [],
      });
      addToast({
        type: "error",
        title: t("remove.error.title"),
        message: t("remove.error.description"),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
      });
    },
  });

  const { mutate: update } = useMutation({
    mutationFn: async ({
      itemId,
      rentalPlan: { committedMonths },
    }: {
      itemId: number;
      rentalPlan: {
        id: number;
        committedMonths: number;
        price: {
          inCents: number;
          currency: string;
        };
      };
    }) => {
      return changeCartRentalPlan({
        itemId,
        orderNumber: cart.orderNumber,
        store: store,
        committedMonths,
      });
    },
    onMutate: async ({
      itemId,
      rentalPlan: { id: planId, committedMonths, price },
    }: {
      itemId: number;
      rentalPlan: {
        id: number;
        committedMonths: number;
        price: {
          inCents: number;
          currency: string;
        };
      };
    }) => {
      const previousItems = items;
      const previousTotal = total;

      const previousItemIndex = items.findIndex((item) => item.id === itemId);
      const previousItemPrice =
        items[previousItemIndex].rentalPlan.price.inCents;
      items[previousItemIndex] = {
        ...items[previousItemIndex],
        rentalPlan: {
          id: String(planId),
          price,
          length: {
            value: committedMonths,
          },
        },
      };

      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        total: {
          ...previousTotal,
          inCents: previousTotal.inCents + price.inCents - previousItemPrice,
        },
        items,
      });

      return { previousItems, previousTotal };
    },
    onError: (error, variables, context) => {
      logger.error("[Cart] failed to update item", {
        store,
        errorMessage: error.message,
        ...context,
        ...variables,
      });
      queryClient.setQueryData<ICartInfo>(cartQueryKey, {
        ...cart,
        total: context?.previousTotal || total,
        items: context?.previousItems || [],
      });
      addToast({
        type: "error",
        title: t("update.error.title"),
        message: t("update.error.description"),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: cartQueryKey,
      });
    },
  });

  const { mutateAsync: reCreate } = useMutation({
    mutationFn: async ({ orderNumber }: { orderNumber: string }) => {
      return reCreateCart({ orderNumber }, store);
    },
    onSuccess: () => {
      const queryKey = getGetOrCreateCartForClerkyKeys(store);
      queryClient.resetQueries({
        queryKey,
      });
      queryClient.refetchQueries({
        queryKey,
      });
    },
    onError: (error, variables, context) => {
      logger.error("[Cart] failed to re-create cart", {
        ...variables,
        store,
        errorMessage: error.message,
        context,
      });
    },
  });

  return (
    <CartContext.Provider
      value={{
        ...cart,
        items,
        isFetching: isLoading,
        fetchError: !!fetchError,
        total,
        totalQuantity,
        reCreate,
        add,
        update,
        remove,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  return useContext(CartContext);
};
